import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { FilePdf, Warning } from "@phosphor-icons/react";

export default function Settings() {
  const { user, logout } = useAuth();
  const [topup, setTopup] = useState(true);
  const [access, setAccess] = useState(null);
  const [history, setHistory] = useState([]);
  const isAdmin = user?.role === "admin";
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/settings").then((r) => setTopup(r.data.demo_topup)).catch(() => {});
    }
    api.get("/payments/access").then((r) => setAccess(r.data)).catch(() => {});
    api.get("/payments/history").then((r) => setHistory(r.data.items || [])).catch(() => {});
  }, [isAdmin]);

  const refresh = () => {
    api.get("/payments/access").then((r) => setAccess(r.data)).catch(() => {});
    api.get("/payments/history").then((r) => setHistory(r.data.items || [])).catch(() => {});
  };

  const flipTopup = async () => {
    const next = !topup;
    try { await api.post("/admin/settings/toggle", { key: "demo_topup", value: next }); setTopup(next); toast.success(`Demo top-up ${next ? "enabled" : "disabled"}`); }
    catch (e) { toast.error("Failed"); }
  };

  const cancelSub = async (session_id) => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current billing period.")) return;
    try { await api.post("/payments/cancel-subscription", { session_id }); toast.success("Subscription will end at period close"); refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const resumeSub = async () => {
    try { await api.post("/payments/resume-subscription"); toast.success("Subscription resumed"); refresh(); }
    catch (e) { toast.error("Failed"); }
  };

  const requestRefund = async (session_id) => {
    const reason = window.prompt("Briefly describe why you're requesting a refund:");
    if (reason === null) return;
    try { await api.post("/payments/request-refund", { session_id, reason }); toast.success("Refund request submitted — we'll respond within 5 business days"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const downloadPdf = async () => {
    try {
      const token = localStorage.getItem("inbox_token");
      const res = await fetch(`${API}/audit-report`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "easymail-audit.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to generate report"); }
  };

  const handleDeleteAccount = async () => {
  setDeleting(true);
  try {
    await api.post("/auth/delete");
    toast.success("Account deleted");
    localStorage.clear();
    window.location.href = "/";
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Failed to delete account");
  }
  setDeleting(false);
};

  const caps = access?.capabilities || {};
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString() : "—";

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-3xl">
      <div className="section-tag mb-2">/ Settings</div>
      <h1 className="font-display text-3xl sm:text-4xl mb-8">Account</h1>

      <div className="border border-border bg-background p-6 mb-6 space-y-3">
  <Row k="Name" v={user?.name || "—"} />
  <Row k="Email" v={user?.email} />
  <Row k="Provider" v={user?.provider || "password"} />
  <Row k="Plan" v={user?.plan || "—"} />
  <Row k="Terms accepted" v={user?.tos_accepted_at ? `${fmtDate(user.tos_accepted_at)} (v${user.tos_version || "—"})` : "—"} />
  {isAdmin && <Row k="Role" v="admin" />}
</div>

      {/* Subscription management */}
      <div className="border border-border bg-background p-6 mb-6">
        <div className="section-tag mb-2">Subscription</div>
        {access ? (
          <>
            <div className="text-sm space-y-1 mb-4">
              <div>Status: <span className="font-mono">{access.subscription_active ? "ACTIVE" : "INACTIVE"}</span></div>
              <div>Plan: <span className="font-mono">{access.plan || "—"}</span></div>
              <div>Access expires: <span className="font-mono">{fmtDate(access.subscription_expires_at)}</span></div>
<div className="text-xs text-muted-foreground mt-1">Access does not renew automatically. Purchase again before this date to continue uninterrupted.</div>
              {access.cancel_at_period_end && (
                <div className="flex items-center gap-2 text-yellow-700 mt-2"><Warning size={14} /> Cancellation pending — access until {fmtDate(access.subscription_expires_at)}</div>
              )}
              <div className="pt-2 text-xs text-muted-foreground">
                Bulk delete: <b>{caps.bulk_delete ? "yes" : "no"}</b> ·
                Auto-rules: <b>{caps.auto_rules ? "yes" : "no"}</b> ·
                Analytics: <b>{caps.analytics ? "yes" : "no"}</b>
                {access.sub_restored_bulk && <span className="ml-2 text-emerald-700">· Special access rule active</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {access.subscription_active && !access.cancel_at_period_end && (
                <Button variant="outline" className="rounded-none" onClick={cancelSub} data-testid="cancel-sub-btn">Cancel subscription</Button>
              )}
              {access.cancel_at_period_end && (
                <Button className="rounded-none btn-brutalist" onClick={resumeSub} data-testid="resume-sub-btn">Resume subscription</Button>
              )}
              <Link to="/pricing"><Button className="rounded-none btn-brutalist" data-testid="manage-plan-btn">Change plan</Button></Link>
            </div>
          </>
        ) : <div className="text-sm text-muted-foreground">Loading…</div>}
      </div>

      {/* Billing history */}
      <div className="border border-border bg-background p-6 mb-6">
        <div className="section-tag mb-3">Billing history</div>
        {history.length === 0 ? (
          <div className="text-sm text-muted-foreground">No transactions yet.</div>
        ) : (
          <div className="space-y-2">
            {history.map((t) => (
              <div key={t.session_id} className="flex items-center justify-between text-sm border-b border-border last:border-0 py-2" data-testid={`txn-${t.session_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{t.type === "pass" ? "Bulk Delete Pass" : `Subscription · ${t.plan_id || ""}`}</div>
                  <div className="text-xs font-mono text-muted-foreground">{fmtDate(t.created_at)} · ${t.amount} {t.currency?.toUpperCase()} · {t.payment_status}</div>
                </div>
                {t.type === "subscription" && t.payment_status === "paid" && (
                  <Button size="sm" variant="ghost" className="rounded-none text-xs" onClick={() => cancelSub(t.session_id)} data-testid={`cancel-sub-${t.session_id}`}>Cancel subscription</Button>
                )}
                {t.payment_status === "paid" && (
                  <Button size="sm" variant="ghost" className="rounded-none text-xs" onClick={() => requestRefund(t.session_id)} data-testid={`refund-${t.session_id}`}>Request refund</Button>
                )}
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-muted-foreground mt-3">Receipts are emailed by Stripe to {user?.email}. See our <Link to="/legal/refunds_policy" className="underline">Refund policy</Link>.</p>
      </div>

      <div className="border border-border bg-background p-6 mb-6">
        <div className="section-tag mb-2">Inbox Audit Report</div>
        <p className="text-sm text-muted-foreground mb-4">Download a typeset PDF of your inbox stats.</p>
        <Button onClick={downloadPdf} className="rounded-none btn-brutalist gap-2" data-testid="download-audit-btn"><FilePdf size={16} /> Download PDF</Button>
      </div>

      {isAdmin && (
        <div className="border border-border bg-background p-6 mb-6">
          <div className="section-tag mb-2">Admin · Demo Top-up</div>
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground max-w-md">When enabled, synthetic emails are added periodically to demo accounts so the UI never looks empty. Turn off in production.</p>
            <Switch checked={topup} onCheckedChange={flipTopup} data-testid="topup-toggle" />
          </div>
        </div>
      )}

      <div className="border border-destructive bg-background p-6">
  <div className="section-tag mb-2 text-destructive">Danger zone</div>
  <p className="text-sm text-muted-foreground mb-4">Log out of this session.</p>
  <Button variant="destructive" className="rounded-none" onClick={logout} data-testid="settings-logout">Log out</Button>

  <div className="border-t border-destructive mt-6 pt-6">
    <p className="text-sm text-muted-foreground mb-4">
      Permanently delete your account, all connected inboxes, emails, and data.
      <strong> This cannot be undone.</strong>
    </p>
    <Button
      variant="outline"
      className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white"
      onClick={() => setShowDeleteConfirm(true)}
      data-testid="delete-account-btn"
    >
      Delete my account
    </Button>
  </div>
</div>

{/* Delete Confirmation Modal */}
{showDeleteConfirm && (
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
    <div className="bg-background border border-destructive p-6 max-w-sm w-full mx-4">
      <div className="section-tag text-destructive mb-2">Confirm deletion</div>
      <p className="text-sm text-muted-foreground mb-6">
        This will permanently delete your account, all connected inboxes, emails, passes, and data.
        <strong> This cannot be undone.</strong>
      </p>
      <div className="flex gap-3">
        <Button
          variant="outline"
          className="flex-1 rounded-none"
          onClick={() => setShowDeleteConfirm(false)}
          disabled={deleting}
        >
          Cancel
        </Button>
        <Button
          variant="destructive"
          className="flex-1 rounded-none"
          onClick={handleDeleteAccount}
          disabled={deleting}
          data-testid="confirm-delete-btn"
        >
          {deleting ? "Deleting…" : "Yes, delete everything"}
        </Button>
      </div>
    </div>
  </div>
)}


      <div data-testid="footer-legal" className="mt-10 py-4 text-center text-xs text-muted-foreground border-t border-border">
        <Link to="/legal" className="underline mx-2">Legal Documents and Policies</Link>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between text-sm border-b border-border last:border-0 py-2">
      <div className="section-tag">{k}</div>
      <div className="font-medium">{v}</div>
    </div>
  );
}
